import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Share2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import Button from '../components/ui/Button';
import { FAST_ID_REPORT_LIST_PAGE_KEY, listReturnPath } from '../utils/listPage';
import {
  FAST_ID_DOMAIN_SLICE_ALL,
  FAST_ID_DOMAIN_SLICE_OPTIONS,
  FastIdReportSections,
  copyFastIdReportLink,
  fetchFastIdReports,
} from '../components/fastId/FastIdReportView';

export default function FastIdReportDetail() {
  const { executionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const listPath = listReturnPath('/fastid-report', location, FAST_ID_REPORT_LIST_PAGE_KEY);

  const [domainSlice, setDomainSlice] = useState(FAST_ID_DOMAIN_SLICE_ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  /** Domain-slice refetch only: keep layout/data visible (no full-page loading flash). */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const dataRef = useRef(null);
  dataRef.current = data;

  useEffect(() => {
    if (!executionId) return undefined;
    let cancelled = false;
    const keepStaleLayout = dataRef.current != null;

    (async () => {
      if (keepStaleLayout) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const result = await fetchFastIdReports(executionId, domainSlice);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load Fast-ID report.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId, domainSlice]);

  const reportFileName = String(data?.report?.filename ?? '').trim();
  const pageTitle = reportFileName
    ? `Fast-ID Report (${reportFileName})`
    : 'Fast-ID Report';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(listPath)}
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" /> FastID Reports
        </button>
        <h1 className="text-xl font-semibold text-gray-900 break-all">{pageTitle}</h1>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 mb-4">
            <div className="min-w-0 flex-1">
              <span className="font-medium text-gray-700">Execution ID:</span>{' '}
              <code className="break-all bg-gray-100 px-2 py-0.5 rounded text-xs">
                {executionId}
              </code>
            </div>
            <div className="w-full sm:w-56 sm:flex-shrink-0">
              <FunctionalSelect
                label="Domain"
                id="fast-id-report-domain"
                value={domainSlice}
                onValueChange={setDomainSlice}
                placeholder="All"
                className="w-full"
              >
                {FAST_ID_DOMAIN_SLICE_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => copyFastIdReportLink(executionId)}
              aria-label="Copy report link"
              title="Copy report link"
              className="sm:flex-shrink-0"
            >
              <Share2 className="h-4 w-4" />
            </Button>
          </div>

          {loading && data == null && <p className="text-sm text-gray-600">Loading report…</p>}
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          {data != null && (
            <div className="relative text-sm text-gray-800 space-y-4">
              {refreshing && (
                <div
                  className="absolute inset-0 z-10 flex justify-center pt-6 bg-white/50 pointer-events-none rounded-md"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <span className="inline-flex items-center gap-2 text-sm text-gray-600 h-fit">
                    <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                    Updating…
                  </span>
                </div>
              )}
              <FastIdReportSections report={data?.report} matchRate={data?.matchRate} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
